/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { useQuery } from '@tanstack/react-query';

// utils
import {
	convertGCO2EToTCO2EWithoutUnit,
	formatDate,
} from '@utils/functions';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';

// lodash
import isNil from 'lodash/isNil';

//types
import { UNITS_PROJECT } from '@config/constants/units-project';
import { TApiParams, TParams } from '@global-types/index';
import {
	formatNumberWithSpaces,
	formatTokenAvailable,
} from '@utils/formats';
import { getTechnologyProject } from '@utils/project-technologies';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';
import {
	TApiResponseGetMeasurabilities,
	TMesurabilityProjectPopulated,
	TParsedMesurabilityProject,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getMeasurabilities = (
	params: TApiParams
): Promise<TApiResponseGetMeasurabilities> => {
	return apiClient.get('/measurabilities', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate:
				'activatedBy, cover, logo, publicSpecs, internalSpecs , sites , sites.meterPoints ,sites.production, entity , organization',
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

export const useGetMeasurabilities = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'databases-measurabilities',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getMeasurabilities({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (
		measurabilities: TMesurabilityProjectPopulated[]
	) =>
		measurabilities.reduce(
			(
				accumulator: TParsedMesurabilityProject[],
				currentItem: TMesurabilityProjectPopulated
			) => {
				const modifiedItem: TParsedMesurabilityProject = {
					...currentItem,
					unit: currentItem?.unit
						? t(
								UNITS_PROJECT.find(
									(unit: { value: string }) =>
										unit.value === currentItem.unit
								)?.label ?? currentItem.unit
							)
						: NOT_APPLICABLE,
					createdAt: formatDate(
						currentItem.createdAt,
						'DD/MM/YYYY'
					),
					activatedAt: !isNil(currentItem.activatedAt)
						? formatDate(
								currentItem.activatedAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					place:
						!isNil(currentItem.location) &&
						!isNil(currentItem.location.place)
							? currentItem.location.place
							: NOT_APPLICABLE,
					entity: !isNil(currentItem.entity)
						? currentItem.entity.name
						: NOT_APPLICABLE,
					organization: !isNil(currentItem.organization)
						? currentItem.organization.name
						: NOT_APPLICABLE,
					activatedBy: !isNil(currentItem.activatedBy)
						? `${currentItem.activatedBy.firstName} ${currentItem.activatedBy.lastName}`
						: NOT_APPLICABLE,
					technology:
						!isNil(currentItem.technology) &&
						!isNil(
							getTechnologyProject(currentItem.technology)
						) &&
						!isNil(
							getTechnologyProject(currentItem.technology)
								?.label
						)
							? t(
									'' +
										getTechnologyProject(
											currentItem.technology
										)?.label
								)
							: NOT_APPLICABLE,
					siteCount:
						currentItem.sites &&
						Array.isArray(currentItem.sites)
							? currentItem.sites.length
							: 0,
					sellPrice: !isNaN(
						currentItem.sellPrice_europertco2eq
					)
						? `${currentItem.sellPrice_europertco2eq} ${t('global.sellPriceUnit')}`
						: NOT_APPLICABLE,
					availableStock: `${
						currentItem?.quantity?.remaining_aco2
							? formatNumberWithSpaces(
									Number(
										formatTokenAvailable(
											convertGCO2EToTCO2EWithoutUnit(
												currentItem.quantity
													.remaining_aco2
											)
										)
									)
								)
							: 0
					} ${getUnitLabel('tco2eq')}`,
					sites:
						!isNil(currentItem.sites) &&
						Array.isArray(currentItem.sites)
							? currentItem.sites.map((_site) => {
									return {
										name: _site.name,
										minExpected:
											!isNil(_site.production) &&
											!isNil(_site.production.minValue)
												? `${_site.production.minValue} ${getUnitLabel('kwh')}`
												: NOT_APPLICABLE,
										maxExpected:
											!isNil(_site.production) &&
											!isNil(_site.production.maxValue)
												? `${_site.production.maxValue} ${getUnitLabel('kwh')}`
												: NOT_APPLICABLE,
										meterPointsCount:
											!isNil(_site.meterPoints) &&
											Array.isArray(_site.meterPoints) &&
											_site.meterPoints.length
												? _site.meterPoints.length
												: 0,
									};
								})
							: [],
				};
				accumulator.push(modifiedItem);

				return accumulator;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isFetching,
		isFetched,
		total: data?.total ? data?.total : 0,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
